import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import PageLoader from '../components/PageLoader/PageLoader';
import ManagerDashboard from '../pages/manager-dashboard';
import Outerlayout from '../layouts/outerLayout';
import Innerlayout from '../layouts/innerLayout';
import EventsLayout from '../layouts/eventsLayout';
import EventCreateStepsLayout from '../layouts/eventCreateStepsLayout';
import { EventCreateStorageCleanup } from '../utils/eventCreateStorageCleanup';
import ScrollToTop from '../components/ScrollToTop/ScrollToTop'; // eslint-disable-line no-unused-vars
import { getCookies, removeCookies } from '../hooks/useCookies';
import OnboardingGuard from '../components/OnboardingGuard/OnboardingGuard';
import Settings from '../pages/settings/settings';
import SettingsApi from '../pages/settings/SettingsApiPage';
import SettingsLayout from '../pages/settings/settingsLayout';
import PageNotFound from '../pages/404/pageNotFound';
import PermissionDenied from '../pages/permission/permission';
import ProfileLayout from '../pages/profile/profileLayout';
import EventHome from '../pages/dashboard/dashboardEvent';
import VendorIndex from '../pages/vendors';
import EventIndex from '../pages/event';
import TransferIndex from '../pages/token-transfer';
import ShowsIndex from '../pages/shows';
import ViewAttendees from '../pages/shows/viewShowAttendees';
import ViewTicket from '../pages/shows/viewTicket';

const EventsPage = lazy(() => import('../pages/events/EventsPage'));
const BulkMessage = lazy(() => import('../pages/bulk-message'));
const UserCouponsPage = lazy(() => import('../pages/user-coupons'));
const RolesAndPermission = lazy(() => import('../pages/roles-and-permission'));
const AcceptManagerInvitation = lazy(() => import('../pages/accept-manager-invitation'));
const SeatBookingAnalytics = lazy(() => import('../pages/seat-booking-analytics'));
const RegistrationAnalytics = lazy(() => import('../pages/registration-analytics'));
const TokenBasedAnalytics = lazy(() => import('../pages/token-based-analytics'));
const RsvpAnalytics = lazy(() => import('../pages/rsvp-analytics'));
const NonSeatBokkingAnalytics = lazy(() => import('../pages/non-seat-bokking-analytics'));
const OrderManagement = lazy(() => import('../pages/order-management'));
const ViewDetails = lazy(() => import('../pages/order-management/viewDetails'));
const ViewRefund = lazy(() => import('../pages/order-management/viewRefund'));
const TokenTransferListing = lazy(() => import('../pages/token-transfer-listing'));
const VendorsListing = lazy(() => import('../pages/vendors-listing'));
const VendorViewDetails = lazy(() => import('../pages/vendors-listing/ViewVendor'));
const PayoutsAndRevenue = lazy(() => import('../pages/payouts-and-revenue'));
const PayoutViewDetails = lazy(() => import('../pages/payouts-and-revenue/viewDetails'));
const PayoutTokenViewDetails = lazy(() => import('../pages/payouts-and-revenue/viewTokenDetails'));
const FaqPage = lazy(() => import('../pages/faq'));
const Contact = lazy(() => import('../pages/faq/contact'));
const Draft = lazy(() => import('../pages/draft'));
const AiPrompt = lazy(() => import('../pages/ai-prompt'));
const AiGenerated = lazy(() => import('../pages/ai-generated'));
const AiDraft = lazy(() => import('../pages/ai-draft'));
const ChooseType = lazy(() => import('../pages/choose-type'));
const AuthHandoff = lazy(() => import('../pages/auth-handoff'));
const EventBasics = lazy(() => import('../pages/event-basics'));
const DateAndTime = lazy(() => import('../pages/date-and-time'));
const TicketAndPricing = lazy(() => import('../pages/ticket-and-pricing'));
const EventSeatLocation = lazy(() => import('../pages/event-seat-location'));
const MediaAndBranding = lazy(() => import('../pages/media-and-branding'));
const Media = lazy(() => import('../pages/media'));
const PaymentMethods = lazy(() => import('../pages/payment-methods'));
const RaffleConfiguration = lazy(() => import('../pages/raffle-configuration'));
const FoodAndBeverages = lazy(() => import('../pages/food-and-beverages'));
const AttendeeRegistration = lazy(() => import('../pages/attendee-registration'));
const TermsAndSettings = lazy(() => import('../pages/terms-and-settings'));
const TermsAndNotes = lazy(() => import('../pages/terms-and-notes'));
const TermsPreview = lazy(() => import('../pages/terms-preview'));
const AdditionalDetails = lazy(() => import('../pages/additional-details'));
const EventPreview = lazy(() => import('../pages/event-preview'));
const DateAndLocation = lazy(() => import('../pages/date-and-location'));
const FormBuilder = lazy(() => import('../pages/form-builder'));
const FormPreview = lazy(() => import('../pages/FormPreview'));
const AddVendors = lazy(() => import('../pages/add-vendors'));
const BuildSchedule = lazy(() => import('../pages/build-schedule'));
const ManagerNotifications = lazy(() => import('../pages/manager-notifications'));
const ShowDetails = lazy(() => import('../pages/show-details'));
const ShowDetailsBySlugRedirect = lazy(() =>
  import('../pages/show-details/ShowDetailsBySlugRedirect')
);
const EventTerms = lazy(() => import('../pages/event-terms'));
const AccountSettings = lazy(() => import('../pages/account-settings'));
const ManagePayment = lazy(() => import('../pages/manage-payment')); // eslint-disable-line no-unused-vars
const EditBusinessDetails = lazy(() => import('../pages/edit-business-details'));
const VendorCreate = lazy(() => import('../pages/vendors/createVendor'));
const VendorEdit = lazy(() => import('../pages/vendors/editVendor'));
const ViewVendor = lazy(() => import('../pages/vendors/viewVendor'));
const ViewEvent = lazy(() => import('../pages/event/viewEvent'));
const EventCreate = lazy(() => import('../pages/event/createEvent'));
const Verification = lazy(() => import('../pages/otps/otpVerify'));
const ResetPasswordOTP = lazy(() => import('../pages/otps/otpReset'));
const DashboardViewVendor = lazy(() => import('../pages/dashboard/viewVendor'));
const ResetPasswordManager = lazy(() => import('../pages/resetPassword/resetPassword'));
const DataDeletion = lazy(() => import('../pages/deletion-request/dataDeletion'));
const DeletionRequest = lazy(() => import('../pages/deletion-request/deletionRequest'));
const TermsAndConditions = lazy(() => import('../pages/terms-and-condition/terms'));
const PrivacyPolicy = lazy(() => import('../pages/privacy-policy/privacy'));
const Login = lazy(() => import('../pages/logIn/logIn'));
const SignInFlow = lazy(() => import('../pages/sign-in-flow')); // eslint-disable-line no-unused-vars
const CreateAccount = lazy(() => import('../pages/sign-in-flow/CreateAccount'));
// eslint-disable-next-line no-unused-vars
const VerifyEmail = lazy(() => import('../pages/sign-in-flow/VerifyEmail'));
const YoureAllSet = lazy(() => import('../pages/sign-in-flow/YoureAllSet'));
const Onboarding = lazy(() => import('../pages/onboarding'));
const Register = lazy(() => import('../pages/register/register'));
const Forgetpassword = lazy(() => import('../pages/passwords/forgetPassword'));
// const Dashboard = lazy(() => import('../pages/dashboard/dashboard'));
const MyForm = lazy(() => import('../components/PhoneNumberBox/PhoneNumberBox'));
const AgGridTable = lazy(() => import('../components/AgTable/AgGridTable'));
// const PageNotFound = lazy(() => import('../pages/404/pageNotFound'));
const ChangePassword = lazy(() => import('../pages/profile/changePassword'));
const Faq = lazy(() => import('../cms/faq'));

export default function Routers() {
  return (
    <Router>
      <>
        <ScrollToTop />
        <EventCreateStorageCleanup />
        <Routes>
          <Route
            path="/showdetails/:slug"
            element={
              <Suspense fallback={<PageLoader />}>
                <ShowDetailsBySlugRedirect />
              </Suspense>
            }
          />
          {/* Legacy dashboard URL (old sidebar, backend menu, bookmarks) */}
          <Route path="/dashboard" element={<Navigate to="/manager-dashboard" replace />} />
          <Route
            path="/show-details/:id"
            element={
              <Suspense fallback={<PageLoader />}>
                <ShowDetails />
              </Suspense>
            }
          />
          <Route
            path="/terms/:slug"
            element={
              <Suspense fallback={<PageLoader />}>
                <ProtectedRoute>
                  <EventTerms />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route element={<Outerlayout />}>
            <Route
              exact
              path="/privacy-policy"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PrivacyPolicy />
                </Suspense>
              }
            />
            <Route
              exact
              path="/terms-and-condition"
              element={
                <Suspense fallback={<PageLoader />}>
                  <TermsAndConditions />
                </Suspense>
              }
            />
            <Route
              exact
              path="/deletion-request"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    {' '}
                    <DeletionRequest />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/accept-manager-invitation"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    <AcceptManagerInvitation />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/data-deletion"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    {' '}
                    <DataDeletion />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    <Onboarding />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/auth-handoff"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AuthHandoff />
                </Suspense>
              }
            />
            <Route
              exact
              path="/login"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    {' '}
                    <Login />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/sign-in-flow"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    <SignInFlow />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/sign-in-flow/create-account"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    <CreateAccount />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/sign-in-flow/verify-email"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    <VerifyEmail />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/sign-in-flow/youre-all-set"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    <YoureAllSet />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/onboarding"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <Onboarding />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/register"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    <Register />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/verify-otp"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    <Verification />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/forgetpassword"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    <Forgetpassword />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/verify-reset"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    {' '}
                    <ResetPasswordOTP />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/reset-password"
              element={
                <Suspense fallback={<PageLoader />}>
                  <UnProtectedRoute>
                    {' '}
                    <ResetPasswordManager />
                  </UnProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/ai-prompt"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AiPrompt />
                </Suspense>
              }
            />
            {/* <Route exact path="/" element={<Dashboard />} /> */}
          </Route>
          <Route element={<Innerlayout />}>
            <Route
              exact
              path="/eventdashboard"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <EventHome />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            {/* <Route
            exact
            path="/dashboard"
            element={
              <Suspense
                fallback={<PageLoader />}
              >
                <ProtectedRoute>
                  {' '}
                  <Home />
                </ProtectedRoute>
              </Suspense>
            }
          /> */}
            <Route
              exact
              path="/vendor"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <VendorIndex />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/vendor/create"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <VendorCreate />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/vendor/edit/:vendorId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <VendorEdit />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/vendor/viewdetails/:vendorId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <ViewVendor />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/event"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <EventIndex />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/shows"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <ShowsIndex />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/shows/view-users/:eventId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <ViewAttendees />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/shows/view-ticket/:ticketId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <ViewTicket />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/event/create"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <EventCreate />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/event/viewdetails/:eventId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <ViewEvent />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/transfer"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <TransferIndex />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/text"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <MyForm />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/table"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <AgGridTable />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/eventdashboard/vendor/:vendorId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    {' '}
                    <DashboardViewVendor />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route element={<SettingsLayout />}>
              <Route
                exact
                path="/settings"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProtectedRoute>
                      {' '}
                      <Settings />
                    </ProtectedRoute>
                  </Suspense>
                }
              />
              <Route
                exact
                path="/api"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProtectedRoute>
                      {' '}
                      <SettingsApi />
                    </ProtectedRoute>
                  </Suspense>
                }
              />
            </Route>
            <Route element={<ProfileLayout />}>
              <Route
                exact
                path="/cms"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProtectedRoute>
                      {' '}
                      <Faq />
                    </ProtectedRoute>
                  </Suspense>
                }
              />

              <Route
                exact
                path="/changePassword"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProtectedRoute>
                      {' '}
                      <ChangePassword />
                    </ProtectedRoute>
                  </Suspense>
                }
              />
            </Route>
          </Route>

          {/* NEW LAYOUT FOR EVENTS */}

          <Route element={<EventsLayout />}>
            <Route
              exact
              path="/events"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <EventsPage />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/bulk-message"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <BulkMessage />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/events/:eventId/user-coupons"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <UserCouponsPage />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/roles-and-permission/:eventId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <RolesAndPermission />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/seat-booking-analytics/:eventId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <SeatBookingAnalytics />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/registration-analytics/:eventId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <RegistrationAnalytics />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/token-based-analytics/:eventId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <TokenBasedAnalytics />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/rsvp-analytics/:eventId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <RsvpAnalytics />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/non-seat-booking-analytics/:eventId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <NonSeatBokkingAnalytics />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/order-management"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <OrderManagement />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/order-management/viewdetails/:ticketId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <ViewDetails />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/order-management/refund/:ticketId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <ViewRefund />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/token-transfer"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <TokenTransferListing />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/vendors"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <VendorsListing />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/vendors/viewdetails/:vendorId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <VendorViewDetails />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/payout"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <PayoutsAndRevenue />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/payout/viewdetails"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <PayoutViewDetails />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/payout/token/viewdetails"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <PayoutTokenViewDetails />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/faq"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <FaqPage />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/contact"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <Contact />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/draft"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <Draft />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/manager-dashboard"
              element={
                <ProtectedRoute>
                  <ManagerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="/manager-notifications"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <ManagerNotifications />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/account-settings"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <AccountSettings />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/manage-payment"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <ManagePayment />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/edit-business-details"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <EditBusinessDetails />
                  </ProtectedRoute>
                </Suspense>
              }
            />
          </Route>

          {/* Event Create Steps Layout */}
          <Route element={<EventCreateStepsLayout />}>
            <Route
              exact
              path="/ai-generated"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AiGenerated />
                </Suspense>
              }
            />
            <Route
              exact
              path="/ai-draft"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AiDraft />
                </Suspense>
              }
            />
            <Route
              exact
              path="/choose-type"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ChooseType />
                </Suspense>
              }
            />
            <Route
              exact
              path="/event-basics"
              element={
                <Suspense fallback={<PageLoader />}>
                  <EventBasics />
                </Suspense>
              }
            />
            <Route
              exact
              path="/event/edit/:eventId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <EventBasics />
                </Suspense>
              }
            />
            <Route
              exact
              path="/date-and-time"
              element={
                <Suspense fallback={<PageLoader />}>
                  <DateAndTime />
                </Suspense>
              }
            />
            <Route
              exact
              path="/ticket-and-pricing"
              element={
                <Suspense fallback={<PageLoader />}>
                  <TicketAndPricing />
                </Suspense>
              }
            />
            <Route
              exact
              path="/event-seat-location"
              element={
                <Suspense fallback={<PageLoader />}>
                  <EventSeatLocation />
                </Suspense>
              }
            />
            <Route
              exact
              path="/media-and-branding"
              element={
                <Suspense fallback={<PageLoader />}>
                  <MediaAndBranding />
                </Suspense>
              }
            />
            <Route
              exact
              path="/media"
              element={
                <Suspense fallback={<PageLoader />}>
                  <Media />
                </Suspense>
              }
            />
            <Route
              exact
              path="/payment-methods"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PaymentMethods />
                </Suspense>
              }
            />
            <Route
              exact
              path="/raffle-configuration"
              element={
                <Suspense fallback={<PageLoader />}>
                  <RaffleConfiguration />
                </Suspense>
              }
            />
            <Route
              exact
              path="/food-and-beverages"
              element={
                <Suspense fallback={<PageLoader />}>
                  <FoodAndBeverages />
                </Suspense>
              }
            />
            <Route
              exact
              path="/attendee-registration"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AttendeeRegistration />
                </Suspense>
              }
            />
            <Route
              exact
              path="/terms-and-settings"
              element={
                <Suspense fallback={<PageLoader />}>
                  <TermsAndSettings />
                </Suspense>
              }
            />
            <Route
              exact
              path="/terms-and-notes"
              element={
                <Suspense fallback={<PageLoader />}>
                  <TermsAndNotes />
                </Suspense>
              }
            />
            <Route
              exact
              path="/terms-preview"
              element={
                <Suspense fallback={<PageLoader />}>
                  <TermsPreview />
                </Suspense>
              }
            />
            <Route
              exact
              path="/additional-details"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdditionalDetails />
                </Suspense>
              }
            />
            <Route
              exact
              path="/event-preview"
              element={
                <Suspense fallback={<PageLoader />}>
                  <EventPreview />
                </Suspense>
              }
            />
            <Route
              exact
              path="/date-and-location"
              element={
                <Suspense fallback={<PageLoader />}>
                  <DateAndLocation />
                </Suspense>
              }
            />
            <Route
              exact
              path="/form-builder"
              element={
                <Suspense fallback={<PageLoader />}>
                  <FormBuilder />
                </Suspense>
              }
            />
            <Route
              exact
              path="/form-preview"
              element={
                <Suspense fallback={<PageLoader />}>
                  <FormPreview />
                </Suspense>
              }
            />
            <Route
              exact
              path="/add-vendors"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute>
                    <AddVendors />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              exact
              path="/build-schedule"
              element={
                <Suspense fallback={<PageLoader />}>
                  <BuildSchedule />
                </Suspense>
              }
            />
          </Route>
          <Route
            exact
            path="*"
            element={
              <Suspense fallback={<PageLoader />}>
                {' '}
                <PageNotFound />
              </Suspense>
            }
          />
          <Route
            exact
            path="/permission"
            element={
              <Suspense fallback={<PageLoader />}>
                <ProtectedRoute>
                  {' '}
                  <PermissionDenied />
                </ProtectedRoute>
              </Suspense>
            }
          />
        </Routes>
      </>
    </Router>
  );
}

// eslint-disable-next-line react/prop-types
function ProtectedRoute({ children }) {
  const location = useLocation();

  // Only show the "cookies cleared" popup if the user previously had an
  // authenticated session. If they were never logged in, silently redirect.
  const hadSession = (() => {
    try {
      return localStorage.getItem('marq_had_session') === '1';
    } catch (_) {
      return false;
    }
  })();

  if (document.cookie && getCookies('Token')) {
    // /onboarding is accessible with just a Token — new managers completing
    // Stripe/PayPal onboarding may not have USERPERMISSION set yet. Stripe
    // does a full-page redirect back to /onboarding?step=3, so we must not
    // strip the token and bounce them away when USERPERMISSION is absent.
    if (location.pathname === '/onboarding') {
      return <OnboardingGuard>{children}</OnboardingGuard>;
    }
    if (getCookies('USERPERMISSION')) {
      return <OnboardingGuard>{children}</OnboardingGuard>;
    }
    // Token present but permissions missing → cookies were partially cleared
    removeCookies('Token');
    removeCookies('refreshToken');
    sessionStorage.setItem('cookiesCleared', true);
    return <Navigate to="/" />;
  }

  // No token — only warn about cleared cookies when a real session existed
  if (hadSession) {
    sessionStorage.setItem('cookiesCleared', true);
  }
  return <Navigate to="/" />;
}

// eslint-disable-next-line react/prop-types
function UnProtectedRoute({ children }) {
  const location = useLocation();

  // Allow a logged-in user to reach the invitation acceptance page so the
  // token is consumed and the event appears in their Events list.
  // /manager-dashboard is the portal's only dashboard, whatever the role cookie says.
  if (getCookies('Token') && location.pathname !== '/accept-manager-invitation') {
    return <Navigate to="/manager-dashboard" />;
  }
  return children;
}
